import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { LocationType } from '~/client/graph'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import MapViewItemDrawnPart from '~/client/src/shared/components/SitemapHelpers/models/SitemapItemDrawnPart'
import MapViewItemType from '~/client/src/shared/enums/MapViewItemType'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import { useCreatableLocationKinds } from '~/client/src/shared/hooks/useCreatableLocationKinds'
import {
  type PictogramIconName,
  pictogramSlugOf,
} from '~/client/src/shared/icons/pictograms'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ICreatableLocationKinds } from '~/client/src/shared/resources/domain/creatableLocationKinds'
import type { InformationPointKind } from '~/client/src/shared/resources/domain/informationPointGroups'

import MapViewSetUpStore from '../../MapViewSetUp.store'
import { ViewType } from '../../stores/MapBoxEditor.store'
import InformationPointPicker from './InformationPointPicker'
import {
  getLocationObjectIcon,
  ILocationListObject,
  locationObjectKey,
  paletteLocationObjects,
} from './locationObjectsHelper'

import Colors from '~/client/src/shared/theme.module.scss'

const ICON_SIZE = 24

interface IProps {
  store?: MapViewSetUpStore
}

interface IViewProps extends IProps {
  creatableKinds: ICreatableLocationKinds
}

@observer
class LocationObjectsPalletView extends React.Component<IViewProps> {
  @observable.ref private pickerAnchor: HTMLElement | null = null

  public constructor(props: IViewProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidUpdate(): void {
    if (
      this.pickerAnchor &&
      !this.objects.flat().some(object => object.opensInformationPointPicker)
    ) {
      this.onPickerOpenChange(false)
    }
  }

  public render() {
    const { isMarkerDragging } = this.props.store.mapBoxEditorStore
    const { creatableAttributeKind, creatableAttributeIcon } =
      this.props.store.mapViewItemsSetupStore
    return (
      <>
        <div
          className={classList({
            'object-pallet absolute tw:pointer-events-none': true,
            'tw:[&>*]:pointer-events-auto': !isMarkerDragging,
            'inactive-element':
              this.props.store.mapViewItemsSetupStore.isSitemapBlocked,
          })}
        >
          {this.objects.map(section => {
            return section.map((object, idx) => {
              return (
                <div
                  className={classList({
                    'object relative': true,
                    'left-rounded': idx === 0,
                    'right-rounded mr10': idx === section?.length - 1,
                  })}
                  key={locationObjectKey(object)}
                >
                  {this.renderObject(object, locationObjectKey(object))}
                  {this.renderObjectChildren(object)}
                </div>
              )
            })
          })}
        </div>
        <InformationPointPicker
          open={!!this.pickerAnchor}
          onOpenChange={this.onPickerOpenChange}
          anchor={this.pickerAnchor}
          placing={{
            kind: creatableAttributeKind,
            iconName: creatableAttributeIcon,
          }}
          onPlace={this.onInformationPointPlaced}
        />
      </>
    )
  }

  private get objects(): ILocationListObject[][] {
    return paletteLocationObjects(this.props.creatableKinds)
  }

  @action.bound
  private onPickerOpenChange(open: boolean) {
    if (!open) {
      this.pickerAnchor = null
    }
  }

  private onInformationPointPlaced = (
    kind: InformationPointKind,
    iconName: MapViewLocationIcon | PictogramIconName,
    isNamedByIcon?: boolean,
  ) => {
    this.onObjectClicked({
      dataObjectType: LocationType.LogisticsObject,
      iconName,
      kind,
      isNamedByIcon,
    })
  }

  private renderObject = (
    object: ILocationListObject,
    key: React.Key,
    tooltipSide?: 'top' | 'bottom' | 'right',
  ): JSX.Element => {
    const isHighlighted = this.shouldHighlightObject(object)
    const color = isHighlighted ? Colors.neutral0 : Colors.neutral60

    const ObjectIcon = getLocationObjectIcon(object)
    // Inert but still hoverable, so its label shows; a root that only opens a flyout of
    // pickable children keeps its look.
    const isDimmed =
      !!object.isDisabled && !object.children?.some(child => !child.isDisabled)

    const content = (
      <div
        key={key}
        className={classList({
          'object-icon': true,
          pointer: !object.isDisabled,
          'tw:cursor-default': !!object.isDisabled,
          'tw:opacity-50': isDimmed,
        })}
        aria-disabled={object.isDisabled}
        onClick={
          object.isDisabled
            ? undefined
            : object.opensInformationPointPicker
              ? this.openPicker
              : this.onObjectClicked.bind(this, object)
        }
      >
        {/* Colour rides CSS: Lucide's color prop sets only stroke, which would leave a
            filled glyph unpainted. A library sign paints its own colours, so its highlight
            rings the tile instead. */}
        <ObjectIcon
          size={ICON_SIZE}
          className={classList({
            'tw:block': true,
            'tw:rounded-sm tw:ring-2 tw:ring-ring':
              isHighlighted && !!pictogramSlugOf(object.iconName),
          })}
          style={{ color }}
        />
        {object.children && <div className="arrow" />}
      </div>
    )

    if (!object.getLabel) {
      return content
    }

    const side = tooltipSide || (!!object?.children && 'top') || 'bottom'
    const reason =
      object.isDisabled && this.props.creatableKinds.isFailed
        ? Localization.translator.locationTypesFailedToLoad
        : null

    return (
      <Tooltip key={key}>
        <TooltipTrigger render={content} />
        <TooltipContent
          side={side}
          className={
            reason ? 'tw:flex-col tw:items-start tw:gap-0.5' : undefined
          }
        >
          {object.getLabel()}
          {reason && <span>{reason}</span>}
        </TooltipContent>
      </Tooltip>
    )
  }

  @action.bound
  private openPicker(event: React.MouseEvent<HTMLElement>) {
    if (!this.props.store?.mapViewItemsSetupStore.isSitemapBlocked) {
      this.pickerAnchor = event.currentTarget
    }
  }

  private renderObjectChildren = (object: ILocationListObject): JSX.Element => {
    if (!object.children) {
      return null
    }

    return (
      <div className="object-children pointer absolute">
        {object.children.map(childObject => {
          return this.renderObject(
            childObject,
            locationObjectKey(childObject),
            'right',
          )
        })}
      </div>
    )
  }

  private onObjectClicked(object: ILocationListObject) {
    if (!this.props.store) {
      return
    }
    const { mapViewItemsSetupStore, isGlobeMode, onViewTypeSelect } =
      this.props.store
    if (mapViewItemsSetupStore.isSitemapBlocked) {
      return
    }
    const { dataObjectType, sitemapItemType, iconName, kind, isNamedByIcon } =
      object

    if (isGlobeMode) {
      onViewTypeSelect(ViewType.Objects)
    }
    if (sitemapItemType && sitemapItemType === MapViewItemType.Line) {
      mapViewItemsSetupStore.createDataLessItem(sitemapItemType)
      mapViewItemsSetupStore.selectSitemapItemDrawnPart(
        MapViewItemDrawnPart.Shape,
      )
      return
    }
    if (sitemapItemType && sitemapItemType === MapViewItemType.TextBox) {
      mapViewItemsSetupStore.enableCreatingText()
      return
    }
    if (dataObjectType) {
      mapViewItemsSetupStore.enableCreatingAttribute(
        dataObjectType,
        iconName,
        kind,
        isNamedByIcon,
      )
      return
    }
  }

  private shouldHighlightObject(object: ILocationListObject) {
    if (!this.props.store) {
      return
    }
    const {
      selectedMapViewItem,
      creatableAttributeType,
      creatableAttributeIcon,
      creatableAttributeKind,
      isTextStickerCreationActive,
    } = this.props.store.mapViewItemsSetupStore

    const { sitemapItemType, dataObjectType, iconName, kind } = object

    if (object.opensInformationPointPicker) {
      return creatableAttributeType === LocationType.LogisticsObject
    }

    if (sitemapItemType === MapViewItemType.TextBox) {
      return isTextStickerCreationActive
    }

    if (
      sitemapItemType &&
      selectedMapViewItem &&
      !selectedMapViewItem.id &&
      selectedMapViewItem.sitemapItem.type === sitemapItemType
    ) {
      return true
    }

    return (
      creatableAttributeType === dataObjectType &&
      creatableAttributeIcon === iconName &&
      creatableAttributeKind === (kind ?? null)
    )
  }
}

function LocationObjectsPallet({ store }: IProps): JSX.Element {
  return (
    <LocationObjectsPalletView
      store={store}
      creatableKinds={useCreatableLocationKinds()}
    />
  )
}

export default observer(LocationObjectsPallet)
